/**
 * Proof strip review cards: horizontal scroll, controls, 5s auto-advance.
 */
class PdpProofReviews extends HTMLElement {
  #track;
  #prevBtn;
  #nextBtn;
  #timer = null;
  #paused = false;
  #reduceMotion = false;
  #resizeObserver;

  connectedCallback() {
    this.#track = this.querySelector("[data-reviews-track]");
    this.#prevBtn = this.querySelector("[data-reviews-prev]");
    this.#nextBtn = this.querySelector("[data-reviews-next]");

    if (!this.#track) {
      return;
    }

    this.#reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    this.#prevBtn?.addEventListener("click", (event) => {
      event.preventDefault();
      this.#pauseTemporarily();
      this.#scrollByCard(-1);
    });

    this.#nextBtn?.addEventListener("click", (event) => {
      event.preventDefault();
      this.#pauseTemporarily();
      this.#scrollByCard(1);
    });

    this.#track.addEventListener(
      "scroll",
      () => {
        this.#syncControls();
      },
      { passive: true }
    );

    this.addEventListener("pointerenter", () => {
      this.#paused = true;
      this.#stopAutoplay();
    });

    this.addEventListener("pointerleave", () => {
      this.#paused = false;
      this.#startAutoplay();
    });

    this.addEventListener("focusin", () => {
      this.#paused = true;
      this.#stopAutoplay();
    });

    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) {
        this.#paused = false;
        this.#startAutoplay();
      }
    });

    if (typeof ResizeObserver !== "undefined") {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#syncControls();
        this.#startAutoplay();
      });
      this.#resizeObserver.observe(this.#track);
    }

    // Wait for layout so overflow/scrollWidth are accurate.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncControls();
        this.#startAutoplay();
      });
    });
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#resizeObserver?.disconnect();
  }

  #maxScroll() {
    return Math.max(0, this.#track.scrollWidth - this.#track.clientWidth);
  }

  #cardStep() {
    const card = this.#track.querySelector(".pdp-proof-strip__quote");
    if (!card) {
      return Math.max(this.#track.clientWidth * 0.8, 240);
    }

    const styles = window.getComputedStyle(this.#track);
    const gap =
      Number.parseFloat(styles.columnGap || styles.gap || "0") || 0;
    return Math.max(card.getBoundingClientRect().width + gap, 200);
  }

  #scrollByCard(direction) {
    if (!this.#track) {
      return;
    }

    const maxScroll = this.#maxScroll();
    if (maxScroll <= 1) {
      return;
    }

    const step = this.#cardStep();
    const current = this.#track.scrollLeft;
    let nextLeft = current + direction * step;

    if (direction > 0 && current >= maxScroll - 4) {
      nextLeft = 0;
    } else if (direction < 0 && current <= 4) {
      nextLeft = maxScroll;
    } else {
      nextLeft = Math.min(Math.max(nextLeft, 0), maxScroll);
    }

    this.#track.scrollTo({
      left: nextLeft,
      behavior: this.#reduceMotion ? "auto" : "smooth",
    });

    // Keep controls in sync even if scroll event is delayed.
    window.setTimeout(() => this.#syncControls(), 350);
  }

  #syncControls() {
    // Always keep buttons usable when content can scroll; we loop at the ends.
    const canScroll = this.#maxScroll() > 1;

    if (this.#prevBtn) {
      this.#prevBtn.disabled = false;
      this.#prevBtn.setAttribute("aria-disabled", canScroll ? "false" : "true");
      this.#prevBtn.classList.toggle("is-inactive", !canScroll);
    }
    if (this.#nextBtn) {
      this.#nextBtn.disabled = false;
      this.#nextBtn.setAttribute("aria-disabled", canScroll ? "false" : "true");
      this.#nextBtn.classList.toggle("is-inactive", !canScroll);
    }
  }

  #startAutoplay() {
    this.#stopAutoplay();

    if (this.#reduceMotion || this.#paused) {
      return;
    }

    if (this.#maxScroll() <= 1) {
      return;
    }

    this.#timer = window.setInterval(() => {
      if (this.#paused || document.hidden) {
        return;
      }
      this.#scrollByCard(1);
    }, 5000);
  }

  #stopAutoplay() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #pauseTemporarily() {
    this.#paused = true;
    this.#stopAutoplay();
    window.setTimeout(() => {
      if (!this.matches(":hover") && !this.contains(document.activeElement)) {
        this.#paused = false;
        this.#startAutoplay();
      }
    }, 8000);
  }
}

if (!window.customElements.get("pdp-proof-reviews")) {
  window.customElements.define("pdp-proof-reviews", PdpProofReviews);
}
